import { useMemo, useState, useImperativeHandle, forwardRef } from "react";
import {
  Stack,
  Box,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import {
  useGetDutyColumnsQuery,
  useUpdateDutyColumnMutation,
  DutyColumn,
  AUTHORITIES,
} from "@/services/product-catalogue";
import DutyColumnsTable from "../duty-columns-table";
import DutyColumnForm from "../duty-column-form";
import DeleteDutyColumnDialog from "../delete-duty-column-dialog";
import AuthorityFilterDropdown from "../authority-filter-dropdown";
import useOrderedDutyColumns from "@/hooks/useOrderedDutyColumns";
import ExclusionListsManager from "../exclusion-lists-manager";
import { tokens } from "@/tokens";

export interface DutyColumnsManagerRef {
  openCreateForm: () => void;
}

const DutyColumnsManager = forwardRef<DutyColumnsManagerRef>((_, ref) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState<DutyColumn | null>(null);
  const [selectedAuthorities, setSelectedAuthorities] = useState<string[]>(AUTHORITIES);
  const [activeSubTab, setActiveSubTab] = useState<number>(0);

  // API hooks
  const {
    data: dutyColumnsData,
    isLoading: isLoadingColumns,
  } = useGetDutyColumnsQuery(
    useMemo(() => ({
      pageNo: 1,
      pageSize: 500,
      // omit filter when all selected
      authorities: selectedAuthorities.length === AUTHORITIES.length ? undefined : (selectedAuthorities as any),
    }), [selectedAuthorities])
  );

  const [updateDutyColumn] = useUpdateDutyColumnMutation();
  const { orderedColumns } = useOrderedDutyColumns();
  const globalOrderIds = useMemo(() => orderedColumns.map(c => c.id), [orderedColumns]);

  const handleCreateColumn = () => {
    setSelectedColumn(null);
    setIsFormOpen(true);
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    openCreateForm: handleCreateColumn,
  }));

  const handleEditColumn = (column: DutyColumn) => {
    setSelectedColumn(column);
    setIsFormOpen(true);
  };

  const handleDeleteColumn = (columnId: string) => {
    const column = dutyColumnsData?.data.find(c => c._id === columnId);
    if (column) {
      setSelectedColumn(column);
      setIsDeleteDialogOpen(true);
    }
  };

  const handleToggleActive = async (columnId: string, isActive: boolean) => {
    try {
      await updateDutyColumn({
        id: columnId,
        dutyColumn: { isActive }
      }).unwrap();
    } catch (error) {
      console.error("Failed to toggle column active state:", error);
    }
  };

  if (isLoadingColumns) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" component="h2">
          Manager
        </Typography>
      </Stack>

      <Tabs
        value={activeSubTab}
        onChange={(_, v) => setActiveSubTab(v)}
        sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: tokens.fontWeight.bold } }}
      >
        <Tab label="Duty Columns" />
        <Tab label="Exclusion Lists" />
      </Tabs>

      {activeSubTab === 0 && (
        <>
          <AuthorityFilterDropdown
            label="Filter by Authorities"
            value={selectedAuthorities}
            onChange={setSelectedAuthorities}
            options={AUTHORITIES}
          />

          {dutyColumnsData && (
            <DutyColumnsTable
              data={dutyColumnsData.data}
              onEditColumn={handleEditColumn}
              onDeleteColumn={handleDeleteColumn}
              onToggleActive={handleToggleActive}
              globalOrderIds={globalOrderIds}
            />
          )}
        </>
      )}

      {activeSubTab === 1 && <ExclusionListsManager />}

      {isFormOpen && (
        <DutyColumnForm
          open={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSuccess={() => setIsFormOpen(false)}
          column={selectedColumn}
          existingColumns={dutyColumnsData?.data}
        />
      )}

      {isDeleteDialogOpen && selectedColumn && (
        <DeleteDutyColumnDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onDeleted={() => setIsDeleteDialogOpen(false)}
          column={selectedColumn}
        />
      )}
    </Stack>
  );
});

export default DutyColumnsManager; 